import type { ReactNode, SVGProps } from 'react';

function Icon({ children, size = 20, ...rest }: { children: ReactNode; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" {...rest}>
      {children}
    </svg>
  );
}

export const Tape = () => (
  <Icon>
    <path d="M3 15.5 15.5 3 21 8.5 8.5 21z" />
    <path d="m7 12 1.6 1.6M10 9l2.2 2.2M13 6l1.6 1.6M7.5 16.5l1 1" />
  </Icon>
);

export const Clean = () => (
  <Icon>
    <path d="M3 7V4h3M21 7V4h-3M3 17v3h3M21 17v3h-3" />
    <path d="M7.5 12s1.7-3.5 4.5-3.5 4.5 3.5 4.5 3.5-1.7 3.5-4.5 3.5S7.5 12 7.5 12z" />
    <circle cx="12" cy="12" r="1.2" />
  </Icon>
);

export const Camera = () => (
  <Icon>
    <path d="M4 8h3l1.6-2.5h6.8L17 8h3v11H4z" />
    <circle cx="12" cy="13.2" r="3.4" />
  </Icon>
);

export const More = () => (
  <Icon>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </Icon>
);

export const Close = () => (
  <Icon>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const Trash = () => (
  <Icon size={18}>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </Icon>
);

export const Show = () => (
  <Icon>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);

export const Recentre = () => (
  <Icon>
    <circle cx="12" cy="12" r="7" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    <circle cx="12" cy="12" r="1.5" />
  </Icon>
);

export const Plus = () => (
  <Icon>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const Undo = () => (
  <Icon size={18}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </Icon>
);

export const Redo = () => (
  <Icon size={18}>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H10a6 6 0 0 0 0 12h3" />
  </Icon>
);

export const Rotate = () => (
  <Icon>
    <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
    <path d="M21 3v5h-5" />
  </Icon>
);

export const Copy = () => (
  <Icon>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h8" />
  </Icon>
);

export const Lock = ({ open = false }: { open?: boolean }) => (
  <Icon>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d={open ? 'M8 11V7a4 4 0 0 1 7.5-2' : 'M8 11V7a4 4 0 0 1 8 0v4'} />
  </Icon>
);

export const Rows = () => (
  <Icon>
    <path d="M4 6h4M10 6h4M16 6h4M4 12h4M10 12h4M16 12h4M4 18h4M10 18h4M16 18h4" />
  </Icon>
);

export const Search = () => (
  <Icon size={16}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const Offline = () => (
  <Icon size={16}>
    <path d="M2 8.5a15 15 0 0 1 4-2.6M9.5 5.2A15 15 0 0 1 22 8.5M5 12a10 10 0 0 1 3.5-2.1M14 10a10 10 0 0 1 5 2M8.5 15.5a5 5 0 0 1 7 0M12 19h.01M3 3l18 18" />
  </Icon>
);

export const Back = () => (
  <Icon>
    <path d="M15 5 8 12l7 7" />
  </Icon>
);

/** The layout's history: every saved version. */
export const Versions = () => (
  <Icon>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
    <path d="M3 4v4h4" />
    <path d="M12 7.5V12l3 2" />
  </Icon>
);

export const Share = () => (
  <Icon>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  </Icon>
);

export const Saved = () => (
  <Icon size={16}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Icon>
);

export const Dots = () => (
  <Icon>
    <circle cx="5" cy="12" r="1.3" fill="currentColor" />
    <circle cx="12" cy="12" r="1.3" fill="currentColor" />
    <circle cx="19" cy="12" r="1.3" fill="currentColor" />
  </Icon>
);

export const Eye = ({ off = false }: { off?: boolean }) => (
  <Icon size={18}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="2.8" />
    {off && <path d="M4 4l16 16" />}
  </Icon>
);
